<template>
  <section class="contact-edit">
    <header>
      <mo-pageheader>
        <template slot="title">{{ isEdit ? 'Modifier un contact' : 'Ajouter un contact' }}</template>
        <template slot="action">
          <div class="buttons-container flex flex-end">
            <ba-button type="button" class="primary" @click.native="$router.go(-1)">{{ $t('action.return') }}</ba-button>
            <ba-button class="primary" @click.native="handleSaveContact()">{{ $t('action.save') }}</ba-button>
          </div>
        </template>
      </mo-pageheader>
    </header>

    <main>
      <ba-card>
        <template slot="header">
          <span>Fiche contact</span>
        </template>
        <template>
          <div class="profile-card">
            <div class="cols inputs">
              <div class="col-2">
                <ba-input type="text" label="Nom" v-model="contact.last_name"></ba-input>
              </div>
              <div class="col-2">
                <ba-input type="text" label="Email" v-model="contact.email"></ba-input>
              </div>
              <div class="col-2">
                <ba-input type="text" label="Téléphone" v-model="contact.phone"></ba-input>
              </div>
              <div class="col-2">
                <ba-select
                  label="Type de contact"
                  :allow-empty="false"
                  :empty-label="'Sélectionner un type'"
                  :disabled="isEdit"
                  v-model="contact.cli_liv"
                >
                  <option :value="1">Contact client</option>
                  <option :value="2">Contact de livraison</option>
                </ba-select>
              </div>

              <div class="col-2 societe-field">
                <mo-search-input
                  v-if="ready"
                  ref="clientSearch"
                  label="Société (recherche par nom ou code client ERP)"
                  :options="clientOptions"
                  :keys="[ 'label', 'erp' ]"
                  :initValue="clientLabel"
                  :min-length="1"
                  :allow-create="false"
                  v-model="selectedClient"
                  @searchText="handleSearchClients"
                ></mo-search-input>
              </div>
              <div class="col-2">
                <ba-input type="text" label="Code Postal" :disabled="true" v-model="clientZipCode"></ba-input>
              </div>
              <div class="col-2">
                <ba-input type="text" label="Ville" :disabled="true" v-model="clientCity"></ba-input>
              </div>
            </div>
          </div>
        </template>
      </ba-card>

      <div class="buttons-container flex flex-end">
        <ba-button class="primary" @click.native="handleSaveContact()">{{ $t('action.save') }}</ba-button>
      </div>
    </main>

    <!-- Email déjà utilisé : confirmation -->
    <modal class="modal" name="contactEmailDuplicate" width="480px" height="auto">
      <ba-card>
        <template slot="header">
          <span>Email déjà utilisé</span>
        </template>
        <template>
          <p>Cet email est déjà utilisé dans cette compagnie par&nbsp;:</p>
          <ul class="email-matches">
            <li v-for="(m, i) in emailMatches" :key="i">
              {{ m.first_name }} {{ m.last_name }}<span v-if="m.company"> — {{ m.company }}</span>
            </li>
          </ul>
          <p>Voulez-vous tout de même enregistrer ce contact&nbsp;?</p>
        </template>
        <template slot="footer">
          <div class="buttons-container flex flex-end">
            <ba-button class="primary" @click.native="cancelEmailDuplicate">{{ $t('action.cancel') }}</ba-button>
            <ba-button class="danger" @click.native="confirmEmailDuplicate">{{ $t('action.confirm') }}</ba-button>
          </div>
        </template>
      </ba-card>
    </modal>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { BaButton, BaCard, BaInput, BaSelect } from '@ab/basic-components';
import MoPageheader from '@/components/layout/nav/mo-pageheader.vue';
import MoSearchInput from '@/components/form/input/search/mo-search-input.vue';

export default {
  name: 'ContactEdit',
  components: {
    BaButton,
    BaCard,
    BaInput,
    BaSelect,
    MoPageheader,
    MoSearchInput
  },
  data() {
    return {
      ready: false,
      skipClientWatch: false,
      selectedClient: '',
      clientSearchTimer: null,
      emailMatches: [],
      clientLabel: '',
      clientZipCode: '',
      clientCity: '',
      contact: {
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        client_id: '',
        cli_liv: ''
      }
    };
  },
  computed: {
    ...mapState({ clientsLabel: state => state.clients.clientsLabel }),
    // Options du sélecteur : on affiche le libellé + le code ERP du client
    clientOptions() {
      return (this.clientsLabel || []).map(c => ({
        ...c,
        label: c.erp ? `${c.label} (ERP: ${c.erp})` : c.label
      }));
    },
    isEdit() {
      return this.$route.params.id != null && this.$route.params.id !== '';
    }
  },
  watch: {
    selectedClient(newVal) {
      if (this.skipClientWatch) {
        this.skipClientWatch = false;
        return;
      }
      if (newVal) {
        this.loadClient(newVal);
      } else {
        this.clientLabel = '';
        this.clientZipCode = '';
        this.clientCity = '';
      }
    }
  },
  async mounted() {
    const id = this.$route.params.id;
    if (id != null && id !== '') {
      const data = await this.$store.dispatch('getContact', id);
      if (!data || !data.id) {
        this.$store.dispatch('displaySnackbar', { text: "Le contact n'a pas été trouvé", mode: 'error' });
        return this.$router.go(-1);
      }
      this.contact = {
        id: data.id,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        client_id: data.client_id || '',
        cli_liv: data.cli_liv || ''
      };

      if (data.client_id) {
        await this.loadClient(data.client_id);
        this.skipClientWatch = true;
        this.selectedClient = data.client_id;
      }
    }
    this.ready = true;
    this.$store.commit('setSpinner', false);
  },
  methods: {
    handleSearchClients(text) {
      // Recherche lancée automatiquement 350 ms après la dernière frappe
      clearTimeout(this.clientSearchTimer);
      this.clientSearchTimer = setTimeout(() => {
        this.$store.dispatch('getClientsByLabel', text);
      }, 350);
    },
    async loadClient(clientId) {
      const data = await this.$store.dispatch('getClient', { client_id: clientId });
      if (data && data.id) {
        const all = (data.Address && data.Address.Client && data.Address.Client.Address) || [];
        const bill = all.find(a => Number(a.type_address_id) === 1)
          || (data.Address && data.Address.Bill)
          || null;
        this.clientLabel = data.label || '';
        this.clientZipCode = bill ? (bill.zip_code || '') : '';
        this.clientCity = bill ? (bill.city || '') : '';
      }
    },
    validate() {
      if (!this.selectedClient) {
        this.$store.dispatch('displaySnackbar', { text: 'Veuillez sélectionner une société (client)', mode: 'error' });
        return false;
      }
      if (this.contact.last_name.trim() === '' || this.contact.email.trim() === '') {
        this.$store.dispatch('displaySnackbar', { text: 'Le nom et l\'email sont obligatoires', mode: 'error' });
        return false;
      }
      if (!this.isEdit && !this.contact.cli_liv) {
        this.$store.dispatch('displaySnackbar', { text: 'Veuillez sélectionner un type de contact', mode: 'error' });
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.contact.email.trim())) {
        this.$store.dispatch('displaySnackbar', { text: 'Veuillez saisir un email valide', mode: 'error' });
        return false;
      }
      return true;
    },
    async handleSaveContact(force = false) {
      if (!this.validate()) return;

      const payload = {
        manage: 1,
        force: force ? 1 : 0,
        client_id: this.selectedClient,
        first_name: this.contact.first_name.trim(),
        last_name: this.contact.last_name.trim(),
        email: this.contact.email.trim(),
        phone: this.contact.phone,
        cli_liv: this.contact.cli_liv
      };

      let data;
      if (this.isEdit) {
        data = await this.$store.dispatch('updateContact', { id: this.contact.id, ...payload });
      } else {
        data = await this.$store.dispatch('createContact', payload);
      }

      if (!data) return;
      // Doublon d'email : on demande confirmation plutôt que de bloquer
      if (data.flag === 'email_exist') {
        this.emailMatches = data.matches || [];
        this.$modal.show('contactEmailDuplicate');
        return;
      }

      this.$store.dispatch('displaySnackbar', { text: this.isEdit ? 'Le contact a été modifié' : 'Le contact a été créé' });
      this.$router.go(-1);
    },
    cancelEmailDuplicate() {
      this.$modal.hide('contactEmailDuplicate');
    },
    confirmEmailDuplicate() {
      this.$modal.hide('contactEmailDuplicate');
      this.handleSaveContact(true);
    }
  }
};
</script>

<style scoped>
.contact-edit .profile-card {
  display: flex;
  align-items: flex-start;
  padding-top: 1rem;
}
.contact-edit .inputs { flex: 1; }
.contact-edit .buttons-container { padding: 1rem; }
</style>

<style>
.contact-edit .societe-field .mo-search-input .input-ctn { width: 100%; }
.contact-edit .societe-field .mo-search-input .input { padding-right: 1.75rem; }
.contact-edit .societe-field .mo-search-input .loader-ctn {
  position: absolute;
  right: 0.5rem;
  top: 0;
  bottom: 0;
  width: auto;
  height: auto;
  margin: 0;
  display: flex;
  align-items: center;
}

.contact-edit .societe-field .mo-search-input .loader {
  margin: 0;
  font-size: 4px;
}
</style>
<style src="@/assets/styles/requests.css"></style>
